import React, { useEffect, useState } from "react";
import {
  FormGroup,
  MenuItem,
  ListItemText,
  ListSubheader,
  SxProps,
  Select,
  Box,
} from "@mui/material";

import { Checkbox, Chip } from "cprime-foundational-components";

interface Application {
  app_id: number;
  app_name: string;
}

interface ValueStream {
  value_stream_id: number;
  value_stream_name: string;
  applications: Application[];
}

interface CustomSelectProps {
  valueStreams: ValueStream[];
  onChange: (selectedValues: {
    app_id: number[];
    value_stream_id: number[];
  }) => void;
  selectedOptions?: { app_id: number[]; value_stream_id: number[] };
  optionsSx?: SxProps; // New prop for styling
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  valueStreams,
  onChange,
  selectedOptions,
  optionsSx,
}) => {
  const allAppIds = valueStreams
    ?.flatMap((vs) => vs?.applications)
    ?.map((app) => app?.app_id);
  const [selectedValues, setSelectedValues] = useState<number[]>(allAppIds);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    if (isInitialLoad) {
      // Initial load - select all
      setSelectedValues(allAppIds);
      setIsInitialLoad(false);

      // Call onChange once during initialization
      const selectedValueStreamIds = valueStreams?.map(
        (valueStream) => valueStream?.value_stream_id,
      );
      onChange({ app_id: allAppIds, value_stream_id: selectedValueStreamIds });
    } else if (selectedOptions?.app_id) {
      // After initial load - use selectedOptions
      if (selectedOptions?.app_id?.length === 0) {
        // Empty array means select all
        setSelectedValues(allAppIds);
      } else {
        // Use provided selected values
        setSelectedValues(selectedOptions?.app_id);
      }
    }
  }, [selectedOptions, valueStreams, isInitialLoad]);

  const handleToggleApplication = (appId: number) => {
    setSelectedValues((prev: any) => {
      const newValues = prev?.includes(appId)
        ? prev?.filter((v: any) => v !== appId)
        : [...(prev || []), appId];
      updateSelection(newValues);
      return newValues;
    });
  };

  const handleToggleValueStream = (applications: Application[]) => {
    setSelectedValues((prev: any) => {
      const allAppIds = applications?.map((app) => app?.app_id);
      const newValues = allAppIds?.every((id) => prev?.includes(id))
        ? prev?.filter((id: any) => !allAppIds?.includes(id)) // Uncheck all
        : [...(prev || []), ...allAppIds]; // Check all
      updateSelection(newValues);
      return newValues;
    });
  };

  const handleSelectAll = () => {
    const newValues =
      selectedValues?.length === allAppIds?.length ? [] : allAppIds;
    setSelectedValues(newValues);
    updateSelection(newValues);
  };

  const updateSelection = (newValues: number[]) => {
    const selectedAppIds = newValues;
    const selectedValueStreamIds = valueStreams
      ?.filter((valueStream) =>
        valueStream?.applications?.some((app) =>
          selectedAppIds?.includes(app?.app_id),
        ),
      )
      ?.map((valueStream) => valueStream?.value_stream_id);

    onChange({
      app_id: selectedAppIds,
      value_stream_id: selectedValueStreamIds,
    });
  };

  useEffect(() => {
    if (selectedValues?.length === 0) {
      // Replace the `&ZeroWidthSpace;` with a default text
      if (selectedValues?.length === 0) {
        const zeroWidthSpaceElement = document.querySelector(
          "[data-testid='custom-select'] span.notranslate",
        );

        // Check if the element exists before updating
        if (zeroWidthSpaceElement) {
          zeroWidthSpaceElement.textContent = "Select Applications";
        }
      }
    }
  }, [selectedValues]);

  return (
    <Box>
      <Select
        multiple
        data-testid="custom-select"
        value={selectedValues}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: "22rem",
              width: "22rem",
              overflowY: "auto",
            },
          },
        }}
        renderValue={(selected) => {
          if (selected?.includes(-1)) return "All";
          const displayedValues = selected as number[];
          if (displayedValues?.length === 0) return "Select Applications";

          return (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 4,
                // justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Chip
                key={
                  displayedValues?.length === selectedValues?.length
                    ? "all-chip"
                    : displayedValues[displayedValues?.length - 1]
                }
                label={
                  displayedValues?.length === allAppIds?.length
                    ? "All"
                    : (() => {
                        const lastSelectedId =
                          displayedValues[displayedValues?.length - 1];
                        const app = valueStreams
                          ?.flatMap((vs) => vs?.applications)
                          ?.find((app) => app?.app_id === lastSelectedId);
                        return app ? app?.app_name : "";
                      })()
                }
                size="small"
                style={{ marginRight: 4 }}
              />
              {displayedValues?.length === allAppIds?.length
                ? "Applications"
                : ""}
              {displayedValues?.length > 1 &&
                displayedValues?.length !== allAppIds?.length && (
                  <span
                    style={{
                      color: "#000",
                      marginLeft: "auto",
                      letterSpacing: "0.00938em",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      lineHeight: "1rem",
                    }}
                  >
                    + {displayedValues?.length - 1} more
                  </span>
                )}
            </div>
          );
        }}
        sx={{
          height: 40,
          "& ?.MuiOutlinedInput-notchedOutline": {
            borderColor: "black",
          },
          ...optionsSx,
        }}
      >
        <MenuItem
          data-testid="select-all-option"
          onClick={handleSelectAll}
          sx={{ display: "flex", alignItems: "left", padding: "8px 16px" }}
        >
          <Checkbox
            checked={selectedValues?.length === allAppIds?.length}
            indeterminate={
              selectedValues?.length > 0 &&
              selectedValues?.length < allAppIds?.length
            }
          />
          <ListItemText
            primary="All"
            primaryTypographyProps={{
              noWrap: true,
              sx: { overflow: "hidden", textOverflow: "ellipsis" },
            }}
          />
        </MenuItem>
        {valueStreams?.map((valueStream) => {
          const allAppIds = valueStream?.applications?.map(
            (app) => app?.app_id,
          );
          const isAllSelected = allAppIds?.every((id) =>
            selectedValues?.includes(id),
          );
          const isSomeSelected =
            allAppIds?.some((id) => selectedValues?.includes(id)) &&
            !isAllSelected;

          return (
            <ListSubheader
              key={valueStream?.value_stream_id}
              disableSticky
              sx={{ backgroundColor: "transparent", padding: 0 }}
            >
              <MenuItem
                onClick={() =>
                  handleToggleValueStream(valueStream?.applications)
                }
                sx={{
                  display: "flex",
                  alignItems: "left",
                  padding: "8px 16px",
                }}
              >
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isSomeSelected}
                />
                <ListItemText
                  primary={valueStream?.value_stream_name}
                  sx={{ color: "#000", fontSize: "0.875rem", fontWeight: 600 }}
                  primaryTypographyProps={{
                    noWrap: true,
                    sx: { overflow: "hidden", textOverflow: "ellipsis" },
                  }}
                />
              </MenuItem>
              <FormGroup sx={{ paddingLeft: 2 }}>
                {valueStream?.applications?.map((app) => (
                  <MenuItem
                    key={app?.app_id}
                    onClick={() => handleToggleApplication(app?.app_id)}
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      width: "100%",
                      pl: 2,
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                      padding: "8px 16px",
                    }}
                  >
                    <Checkbox checked={selectedValues?.includes(app?.app_id)} />
                    <ListItemText
                      primary={app?.app_name}
                      sx={{
                        color: "#000",
                        fontSize: "0.875rem",
                        fontWeight: 600,
                      }}
                      primaryTypographyProps={{
                        noWrap: true,
                        sx: { overflow: "hidden", textOverflow: "ellipsis" },
                      }}
                    />
                  </MenuItem>
                ))}
              </FormGroup>
            </ListSubheader>
          );
        })}
      </Select>
    </Box>
  );
};

export default CustomSelect;
